/**
 * Подписка на сигналы «Выгодных зон PRO».
 *
 * Индикатор на графике привязан к тому, что открыто сейчас: сменил
 * инструмент — он считается по новому. Уведомление так не умеет: чтобы
 * прийти при закрытой вкладке, серверу нужно знать инструмент,
 * таймфрейм и порог заранее и хранить их у себя.
 *
 * Поэтому подписка — отдельная запись, а не свойство индикатора. В ней
 * лежит слепок настроек на момент включения: тот же порог и тот же
 * состав счёта, по которым рисуется отметка. Пока подписка включена,
 * терминал переписывает слепок при каждой правке настроек — иначе
 * человек поднял бы порог на графике, а уведомления продолжили бы
 * приходить по старому, и понять это было бы неоткуда.
 *
 * Хранится в профиле пользователя, поэтому разбирается с проверкой:
 * профиль живёт дольше кода, и запись прошлой версии не должна ронять
 * остальные.
 */
import { isTimeframe } from '@specter/types';
const DEFAULT_OPTIONS = {
    threshold: 90,
    closedOnly: true,
    requireOi: true,
    usdtd: { on: true, weight: 10 },
    premium: { on: true, weight: 10 },
    funding: { on: true, weight: 5 },
};
const DEFAULT_SHAPE = { levels: 400, age: 2500 };
/** Имя источника в ленте и в уведомлении. */
export const ZONES_WATCH_NAME = 'Выгодные зоны PRO';
/**
 * Подписки из профиля.
 *
 * Непонятная запись пропускается молча: подписка, которой разбор не
 * понял, лучше не сработает, чем сработает наугад.
 */
export function zonesWatchesFrom(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    const seen = new Set();
    for (const entry of raw) {
        const watch = asZonesWatch(entry);
        if (!watch || seen.has(watch.id))
            continue;
        seen.add(watch.id);
        out.push(watch);
    }
    return out;
}
export function asZonesWatch(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const raw = value;
    if (typeof raw.id !== 'string' || raw.id === '')
        return null;
    if (typeof raw.venue !== 'string' || raw.venue === '')
        return null;
    if (typeof raw.symbol !== 'string' || raw.symbol === '')
        return null;
    if (!isTimeframe(raw.timeframe))
        return null;
    return {
        id: raw.id,
        venue: raw.venue,
        symbol: raw.symbol,
        timeframe: raw.timeframe,
        options: optionsFrom(raw.options),
        shape: shapeFrom(raw.shape),
        createdAt: typeof raw.createdAt === 'number' ? raw.createdAt : 0,
    };
}
/*
 * Настройки восстанавливаются по полю, а не целиком.
 *
 * Запись, сделанная прошлой версией, знает не все поля. Отвергнуть её
 * значило бы выключить человеку уведомления молча; взять её как есть —
 * считать по неполным настройкам. Поэтому каждое поле проверяется
 * отдельно, а недостающее берётся из умолчаний индикатора.
 */
function optionsFrom(value) {
    if (typeof value !== 'object' || value === null)
        return DEFAULT_OPTIONS;
    const raw = value;
    return {
        threshold: clamp(raw.threshold, DEFAULT_OPTIONS.threshold, 10, 100),
        closedOnly: typeof raw.closedOnly === 'boolean' ? raw.closedOnly : true,
        requireOi: typeof raw.requireOi === 'boolean' ? raw.requireOi : true,
        usdtd: blockFrom(raw.usdtd, DEFAULT_OPTIONS.usdtd),
        premium: blockFrom(raw.premium, DEFAULT_OPTIONS.premium),
        funding: blockFrom(raw.funding, DEFAULT_OPTIONS.funding),
    };
}
function blockFrom(value, fallback) {
    if (typeof value !== 'object' || value === null)
        return fallback;
    const raw = value;
    return {
        on: typeof raw.on === 'boolean' ? raw.on : fallback.on,
        weight: clamp(raw.weight, fallback.weight, 0, 100),
    };
}
function shapeFrom(value) {
    if (typeof value !== 'object' || value === null)
        return DEFAULT_SHAPE;
    const raw = value;
    return {
        levels: clamp(raw.levels, DEFAULT_SHAPE.levels, 50, 2000),
        age: clamp(raw.age, DEFAULT_SHAPE.age, 100, 20_000),
    };
}
function clamp(value, fallback, min, max) {
    if (typeof value !== 'number' || !Number.isFinite(value))
        return fallback;
    return Math.min(max, Math.max(min, value));
}
//# sourceMappingURL=zones-watch.js.map